/**
 * Project settings validation for the inline messages in Project Settings,
 * mirroring `prism check` (prism_core validate.rs, validate_post.rs,
 * validate_physics.rs, validate_build.rs), plus the pure helpers behind the
 * Quality presets, the Layer Collision Matrix and the Tags & Layers editor.
 *
 * Pure module (no DOM, no React): tested in Node.
 */

export type Obj = Record<string, unknown>;
export const isObj = (v: unknown): v is Obj => v !== null && typeof v === 'object' && !Array.isArray(v);

export interface SettingsIssue {
  /** `settings`-relative path: `quality.shadowCascades`, `layers[2]`, `physics.ignoreCollisions[0]`. */
  path: string;
  level: 'error' | 'warning';
  message: string;
}

/** Most layers a project can define (prism_core physics::MAX_LAYERS). */
export const MAX_LAYERS = 32;
export const DEFAULT_LAYER = 'Default';
export const MSAA_VALUES = [1, 2, 4, 8] as const;

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const isInt = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v);

/* -------------------------------- validation ------------------------------ */

export function validateSettings(settings: Obj): SettingsIssue[] {
  const out: SettingsIssue[] = [];
  const err = (path: string, message: string) => out.push({ path, level: 'error', message });
  const warn = (path: string, message: string) => out.push({ path, level: 'warning', message });

  const layers = layerList(settings);
  if (settings.layers !== undefined) validateLayers(settings.layers, err, warn);
  if (settings.tags !== undefined) validateNames(settings.tags, 'tags', 'tag', err);

  const q = settings.quality;
  if (isObj(q)) {
    const range = (key: string, lo: number, hi: number, hint: string, integer = false) => {
      const v = q[key];
      if (v === undefined) return;
      const n = num(v);
      if (n === null || n < lo || n > hi || (integer && !Number.isInteger(n))) {
        err(`quality.${key}`, `must be ${integer ? 'a whole number' : 'a number'} in ${lo}..${hi} (${hint})`);
      }
    };
    range('shadowCascades', 1, 4, '1..4 cascades', true);
    range('renderScale', 0.25, 2, '1 = native, 0.5 = half resolution');
    range('maxLights', 1, 256, 'the engine limit is 256');
    range('anisotropy', 1, 16, '1 = off');
    range('frameRateCap', 0, 1000, '0 = unlimited');
    range('shadowResolution', 256, 8192, 'shadow atlas pixels', true);
    range('shadowFade', 0, 1, 'fraction of the shadow distance');
    range('jobThreads', -1, 256, '-1 = automatic, 0 = main thread', true);
    range('maxLodLevel', 0, 16, '0 = all levels', true);
    for (const key of ['shadowDistance', 'lodBias', 'drawDistance', 'minScreenSize', 'uploadBudgetMs']) {
      const v = q[key];
      if (v !== undefined && (num(v) === null || (v as number) < 0)) err(`quality.${key}`, 'must be a number >= 0');
    }
    if (q.msaa !== undefined && !MSAA_VALUES.includes(q.msaa as 1)) err('quality.msaa', 'must be 1 (off), 2, 4 or 8');
    const aniso = num(q.anisotropy);
    if (aniso !== null && aniso >= 1 && aniso <= 16 && ![1, 2, 4, 8, 16].includes(aniso)) {
      warn('quality.anisotropy', 'GPUs support 1, 2, 4, 8 or 16; other values round down');
    }
    if (q.cascadeSplits !== undefined) {
      const splits = q.cascadeSplits;
      const cascades = isInt(q.shadowCascades) ? q.shadowCascades : 4;
      if (!Array.isArray(splits) || !splits.every((s) => num(s) !== null)) {
        err('quality.cascadeSplits', 'must be an array of numbers like [0.1, 0.3, 0.6]');
      } else if (splits.length > 0) {
        if (splits.length !== cascades - 1) {
          warn('quality.cascadeSplits', `has ${splits.length} split${splits.length === 1 ? '' : 's'}; ${cascades} cascades use ${cascades - 1} (empty = automatic)`);
        }
        const values = splits as number[];
        if (values.some((s) => s <= 0 || s >= 1) || values.some((s, i) => i > 0 && s <= values[i - 1]!)) {
          err('quality.cascadeSplits', 'must be ascending fractions of the shadow distance between 0 and 1');
        }
      }
    }
  }

  const r = settings.render;
  if (isObj(r)) {
    if (r.msaa !== undefined && !MSAA_VALUES.includes(r.msaa as 1)) warn('render.msaa', 'should be 1, 2, 4 or 8');
    if (r.pixelsPerUnit !== undefined && !((num(r.pixelsPerUnit) ?? 0) > 0)) err('render.pixelsPerUnit', 'must be a positive number (16, 32, 100...)');
    const fov = num(r.fov);
    if (r.fov !== undefined && (fov === null || fov <= 0 || fov >= 180)) err('render.fov', 'must be a number of degrees between 0 and 180');
  }

  const p = settings.physics;
  if (isObj(p)) {
    if (p.gravity !== undefined && !(Array.isArray(p.gravity) && p.gravity.length === 3 && p.gravity.every((g) => num(g) !== null))) {
      err('physics.gravity', 'must be 3 numbers like [0, -9.81, 0]');
    }
    for (const [key, lo, hi] of [
      ['solverIterations', 1, 64],
      ['stabilizationIterations', 0, 64],
    ] as const) {
      const v = p[key];
      if (v !== undefined && !(isInt(v) && v >= lo && v <= hi)) err(`physics.${key}`, `must be a whole number ${lo}..${hi}`);
    }
    for (const key of ['sleepThreshold', 'sleepAngularThreshold', 'sleepTime']) {
      if (p[key] !== undefined && num(p[key]) === null) err(`physics.${key}`, 'must be a number (negative = never sleep)');
    }
    if (p.ignoreCollisions !== undefined) {
      if (!Array.isArray(p.ignoreCollisions)) {
        err('physics.ignoreCollisions', 'must be a list of layer pairs');
      } else {
        p.ignoreCollisions.forEach((pair, i) => {
          if (!Array.isArray(pair) || pair.length !== 2 || !pair.every((n) => typeof n === 'string')) {
            err(`physics.ignoreCollisions[${i}]`, 'must be a pair of layer names like ["Player", "Ghost"]');
            return;
          }
          for (const name of pair as string[]) {
            if (!layers.includes(name) && name !== DEFAULT_LAYER) err(`physics.ignoreCollisions[${i}]`, `'${name}' is not a layer of this project`);
          }
        });
      }
    }
  }
  return out;
}

function validateNames(
  value: unknown,
  path: string,
  what: string,
  err: (path: string, message: string) => void,
): string[] {
  if (!Array.isArray(value)) {
    err(path, `must be a list of ${what} names`);
    return [];
  }
  const seen = new Set<string>();
  const names: string[] = [];
  value.forEach((n, i) => {
    if (typeof n !== 'string') err(`${path}[${i}]`, 'must be a string');
    else if (!n.trim()) err(`${path}[${i}]`, `is empty; give the ${what} a name`);
    else if (seen.has(n)) err(`${path}[${i}]`, `'${n}' is listed twice`);
    else {
      seen.add(n);
      names.push(n);
    }
  });
  return names;
}

function validateLayers(value: unknown, err: (path: string, message: string) => void, warn: (path: string, message: string) => void): void {
  const names = validateNames(value, 'layers', 'layer', err);
  if (!Array.isArray(value)) return;
  if (value.length === 0) err('layers', 'is empty; list at least ["Default"]');
  if (value.length > MAX_LAYERS) err('layers', `has ${value.length} layers; at most ${MAX_LAYERS} are allowed`);
  if (names.length > 0 && !names.includes(DEFAULT_LAYER)) {
    warn('layers', `has no "Default" layer; entities without a layer component use the first layer ('${names[0]}')`);
  }
}

/** Issues for one field (`quality.msaa`) or anything under a prefix (`layers`). */
export function issuesAt(issues: readonly SettingsIssue[], path: string): SettingsIssue[] {
  return issues.filter((i) => i.path === path || i.path.startsWith(`${path}.`) || i.path.startsWith(`${path}[`));
}

/* ------------------------------ quality presets --------------------------- */

export interface QualityPreset {
  id: 'low' | 'medium' | 'high' | 'ultra';
  label: string;
  /** `settings.quality` keys the preset sets (others are left alone). */
  quality: Obj;
  /** Temporal anti-aliasing (`settings.render.postProcess.temporalAntiAliasing.enabled`). */
  taa: boolean;
}

/** High = the engine defaults (prism_core QualitySettings::default). */
export const QUALITY_PRESETS: readonly QualityPreset[] = [
  {
    id: 'low',
    label: 'Low',
    taa: false,
    quality: { shadowDistance: 40, shadowCascades: 1, shadowResolution: 1024, lodBias: 0.5, maxLodLevel: 1, drawDistance: 300, minScreenSize: 4, msaa: 1, renderScale: 0.75, maxLights: 16, anisotropy: 1 },
  },
  {
    id: 'medium',
    label: 'Medium',
    taa: false,
    quality: { shadowDistance: 80, shadowCascades: 2, shadowResolution: 2048, lodBias: 0.75, maxLodLevel: 0, drawDistance: 600, minScreenSize: 2, msaa: 2, renderScale: 1, maxLights: 32, anisotropy: 4 },
  },
  {
    id: 'high',
    label: 'High',
    taa: true,
    quality: { shadowDistance: 150, shadowCascades: 4, shadowResolution: 4096, lodBias: 1, maxLodLevel: 0, drawDistance: 0, minScreenSize: 0, msaa: 4, renderScale: 1, maxLights: 64, anisotropy: 8 },
  },
  {
    id: 'ultra',
    label: 'Ultra',
    taa: true,
    quality: { shadowDistance: 250, shadowCascades: 4, shadowResolution: 8192, lodBias: 1.5, maxLodLevel: 0, drawDistance: 0, minScreenSize: 0, msaa: 8, renderScale: 1, maxLights: 128, anisotropy: 16 },
  },
];

/** Engine defaults of `settings.quality` (absent keys read as these). */
export const QUALITY_DEFAULTS: Readonly<Obj> = {
  shadowDistance: 150,
  shadowCascades: 4,
  shadowResolution: 4096,
  shadowFade: 0.85,
  lodBias: 1,
  maxLodLevel: 0,
  drawDistance: 0,
  minScreenSize: 0,
  jobThreads: -1,
  uploadBudgetMs: 4,
  msaa: 4,
  renderScale: 1,
  vsync: true,
  frameRateCap: 0,
  maxLights: 64,
  anisotropy: 8,
};

/** `settings.quality` with a preset applied (cascade splits reset to automatic). */
export function applyQualityPreset(quality: Obj | undefined, preset: QualityPreset): Obj {
  const next: Obj = { ...(quality ?? {}), ...preset.quality };
  delete next.cascadeSplits;
  return next;
}

/** The preset the quality settings currently match (absent keys = engine defaults). */
export function matchQualityPreset(quality: Obj | undefined): QualityPreset['id'] | null {
  const q = quality ?? {};
  const found = QUALITY_PRESETS.find((p) => Object.entries(p.quality).every(([k, v]) => (q[k] ?? QUALITY_DEFAULTS[k]) === v));
  return found?.id ?? null;
}

/** Reads `settings.render.postProcess.temporalAntiAliasing.enabled`. */
export function taaEnabled(render: unknown): boolean {
  const pp = isObj(render) ? render.postProcess : undefined;
  const taa = isObj(pp) ? pp.temporalAntiAliasing : undefined;
  return isObj(taa) && taa.enabled === true;
}

/** `settings.render` with TAA switched on/off (other post settings kept). */
export function withTaa(render: unknown, enabled: boolean): Obj {
  const r: Obj = isObj(render) ? { ...render } : {};
  const pp: Obj = isObj(r.postProcess) ? { ...r.postProcess } : {};
  const taa: Obj = isObj(pp.temporalAntiAliasing) ? { ...pp.temporalAntiAliasing } : {};
  taa.enabled = enabled;
  pp.temporalAntiAliasing = taa;
  r.postProcess = pp;
  return r;
}

/* ----------------------------- layers and matrix -------------------------- */

/** The project's layers (`settings.layers`, default `["Default"]`), capped at 32. */
export function layerList(settings: Obj): string[] {
  const l = settings.layers;
  if (Array.isArray(l) && l.length > 0 && l.every((x) => typeof x === 'string')) return (l as string[]).slice(0, MAX_LAYERS);
  return [DEFAULT_LAYER];
}

export type LayerPair = [string, string];

function pairsOf(ignore: unknown): LayerPair[] {
  if (!Array.isArray(ignore)) return [];
  return ignore.filter((p): p is LayerPair => Array.isArray(p) && p.length === 2 && p.every((n) => typeof n === 'string'));
}

/** Whether layers `a` and `b` collide under `ignoreCollisions` (pairs are unordered). */
export function layersCollide(ignore: unknown, a: string, b: string): boolean {
  return !pairsOf(ignore).some(([x, y]) => (x === a && y === b) || (x === b && y === a));
}

/**
 * The collision matrix (`matrix[i][j]` = layers i and j collide, symmetric)
 * from `ignoreCollisions`; unknown layer names are skipped like the engine does.
 */
export function collisionMatrix(layers: readonly string[], ignore: unknown): boolean[][] {
  const matrix = layers.map(() => layers.map(() => true));
  for (const [a, b] of pairsOf(ignore)) {
    const i = layers.indexOf(a);
    const j = layers.indexOf(b);
    if (i < 0 || j < 0) continue;
    matrix[i]![j] = false;
    matrix[j]![i] = false;
  }
  return matrix;
}

/**
 * `ignoreCollisions` for a matrix: one `[lower, higher]` pair (by layer order)
 * per unchecked cell, in layer order. Pairs naming layers that are not in
 * `layers` are kept from `previous` (the matrix cannot show them).
 */
export function ignoreFromMatrix(layers: readonly string[], matrix: readonly (readonly boolean[])[], previous: unknown = []): LayerPair[] {
  const out: LayerPair[] = pairsOf(previous).filter(([a, b]) => !layers.includes(a) || !layers.includes(b));
  for (let i = 0; i < layers.length; i++) {
    for (let j = i; j < layers.length; j++) {
      if (matrix[i]?.[j] === false || matrix[j]?.[i] === false) out.push([layers[i]!, layers[j]!]);
    }
  }
  return out;
}

/** `ignoreCollisions` with one pair set to collide or not (other pairs keep their order). */
export function setLayerCollision(layers: readonly string[], ignore: unknown, a: string, b: string, collide: boolean): LayerPair[] {
  const rest = pairsOf(ignore).filter(([x, y]) => !((x === a && y === b) || (x === b && y === a)));
  if (collide) return rest;
  const [first, second] = layers.indexOf(a) <= layers.indexOf(b) ? [a, b] : [b, a];
  return [...rest, [first, second]];
}

/** `ignoreCollisions` after renaming a layer (`to` = null removes its pairs). */
export function renameLayerInPairs(ignore: unknown, from: string, to: string | null): LayerPair[] {
  return pairsOf(ignore).flatMap((pair): LayerPair[] => {
    if (!pair.includes(from)) return [pair];
    if (to === null) return [];
    return [[pair[0] === from ? to : pair[0], pair[1] === from ? to : pair[1]]];
  });
}

/** A name not in `names` (`Layer 8`, `Layer 8 (2)`...). */
export function uniqueName(names: readonly string[], base: string): string {
  if (!names.includes(base)) return base;
  for (let i = 2; ; i++) {
    const candidate = `${base} (${i})`;
    if (!names.includes(candidate)) return candidate;
  }
}
